import {
  OrderHistoryDto,
  OrderHistoryDetailDto,
  OrderLineHistoryDto,
  OrderHistoryExportParams
} from '../types/orderHistory';
import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class OrderService {

  /**
   * Get order history (master-detail view)
   * @param fromDate Start date as YYYY-MM-DD string (optional, defaults to 30 days ago)
   * @param toDate End date as YYYY-MM-DD string (optional, defaults to today)
   * @returns Array of order history DTOs with lines
   */
  async getOrdersHistory(fromDate?: string, toDate?: string, warehouse?: string): Promise<OrderHistoryDto[]> {
    const params = new URLSearchParams();

    if (fromDate) {
      params.append('fromDate', fromDate);
    }
    if (toDate) {
      params.append('toDate', toDate);
    }
    if (warehouse) {
      params.append('warehouse', warehouse);
    }

    const url = `${API_BASE_URL}/orders/history${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Kun lagersjefer og administratorer har tilgang til ordrehistorikk');
      }
      const errorBody = await response.text();
      throw new Error(errorBody || `Kunne ikke hente ordrehistorikk (${response.status})`);
    }

    return response.json();
  }

  /**
   * Get order history in flat view (all lines without grouping)
   * @param fromDate Start date as YYYY-MM-DD string (optional, defaults to 30 days ago)
   * @param toDate End date as YYYY-MM-DD string (optional, defaults to today)
   * @returns Array of order line history DTOs
   */
  async getOrderLinesHistoryFlat(fromDate?: string, toDate?: string, warehouse?: string): Promise<OrderLineHistoryDto[]> {
    const params = new URLSearchParams();

    if (fromDate) {
      params.append('fromDate', fromDate);
    }
    if (toDate) {
      params.append('toDate', toDate);
    }
    if (warehouse) {
      params.append('warehouse', warehouse);
    }

    const url = `${API_BASE_URL}/orders/history/flat${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Kun lagersjefer og administratorer har tilgang til ordrehistorikk');
      }
      const errorBody = await response.text();
      throw new Error(errorBody || `Kunne ikke hente ordrelinjehistorikk (${response.status})`);
    }

    return response.json();
  }

  /**
   * Get detailed information for a specific order
   * @param orderNo Order number
   * @returns Order history detail DTO
   */
  async getOrderHistoryDetail(orderNo: number): Promise<OrderHistoryDetailDto> {
    const url = `${API_BASE_URL}/orders/${orderNo}/history`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Kun lagersjefer og administratorer har tilgang til ordrehistorikk');
      }
      if (response.status === 404) {
        throw new Error(`Ordre ${orderNo} ble ikke funnet`);
      }
      throw new Error(`Kunne ikke hente ordredetaljer: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Export order history to Excel or CSV
   * @param params Export parameters including date range, format, and view type
   * @returns Blob containing the exported file
   */
  async exportOrderHistory(params: OrderHistoryExportParams): Promise<Blob> {
    const queryParams = new URLSearchParams();

    if (params.fromDate) {
      queryParams.append('fromDate', params.fromDate);
    }
    if (params.toDate) {
      queryParams.append('toDate', params.toDate);
    }
    queryParams.append('format', params.format);
    queryParams.append('view', params.view);
    if (params.warehouse) {
      queryParams.append('warehouse', params.warehouse);
    }

    const url = `${API_BASE_URL}/orders/history/export?${queryParams.toString()}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Kun lagersjefer og administratorer har tilgang til ordrehistorikk');
      }
      throw new Error(`Kunne ikke eksportere data: ${response.statusText}`);
    }

    return response.blob();
  }

  /**
   * Download exported file
   * @param blob The file blob
   * @param filename The filename to save as
   */
  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

export default new OrderService();
